import React, { useState } from "react";
import { Box, Typography, useMediaQuery, Button } from "@mui/material";
import { keyframes } from "@emotion/react";
import axios from "axios";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { salesLogoutUser } from "../../store/actions/salesActions";
import { useSalesNav } from "../layout/sales/SalesLayout";

// Define the keyframes for the slide-down animation
const slideDown = keyframes`
  0% {
    transform: translateY(-50%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

const SalesSessionWillExpireBox = ({ willExpireSoon, setWillExpireSoon }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up("lg"));
  const { isNavOpen, sideNavWidth } = useSalesNav();
  const [extendLoading, setExtendLoading] = useState(false);

  const handleExtendSession = async () => {
    try {
      setExtendLoading(true);
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/sales/extend-session`,
        {},
        { withCredentials: true }
      );

      if (resp?.data?.success) {
        // Update sessionStorage with new temp_token
        if (resp.data.data?.temp_token) {
          sessionStorage.setItem(
            "salesUser",
            JSON.stringify({
              temp_token: resp.data.data.temp_token,
              timestamp: Date.now(),
            })
          );
        }
        setWillExpireSoon(false);
      }
    } catch (error) {
      // Check if the error is a 401 Unauthorized
      if (error.response && error.response.status === 401) {
        // Logout and navigate to login
        await dispatch(salesLogoutUser());
        navigate("/sales/login");
      }
    } finally {
      setExtendLoading(false);
    }
  };

  const handleDontExtend = () => {
    setWillExpireSoon(false);
  };

  return (
    <>
      {willExpireSoon && (
        <Box
          sx={{
            position: "fixed",
            top: { xs: "10px", sm: "20px" },
            left: {
              xs: "10px",
              sm: isNavOpen && !lgUp ? `${sideNavWidth + 10}px` : "50%",
              md:
                isNavOpen && lgUp
                  ? `calc(${sideNavWidth}px + (100% - ${sideNavWidth}px) / 2)`
                  : "50%",
            },
            right: { xs: "10px", sm: "auto" },
            transform: {
              xs: "none",
              sm: isNavOpen && !lgUp ? "none" : "translateX(-50%)",
              md: "translateX(-50%)",
            },
            width: { xs: "calc(100% - 42px)", sm: "auto", md: "560px" },
            maxWidth: { xs: "100%", sm: "90vw", md: "560px" },
            backgroundColor: "#ffe1db",
            boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.4)",
            borderRadius: "8px",
            padding: { xs: "12px", sm: "16px" },
            zIndex: 1300,
            animation: `${slideDown} 0.5s ease-out`,
            transition: "left 0.3s ease-in-out, transform 0.3s ease-in-out",
          }}
        >
          <Box sx={{ position: "relative", px: { xs: 1, sm: 2 } }}>
            <Typography
              align="center"
              variant={lgUp ? "h5" : "h6"}
              color="error"
              sx={{
                mx: { xs: 1, sm: 2 },
                fontSize: { xs: "1.1rem", sm: "1.5rem" },
              }}
            >
              Your Session Will Expire Soon
            </Typography>

            <Typography
              variant="body2"
              fontWeight="bold"
              align="center"
              color="primary"
              sx={{
                mt: { xs: 1.5, sm: 2 },
                fontSize: { xs: "0.875rem", sm: "1rem" },
                px: { xs: 1, sm: 0 },
              }}
            >
              Your sales session will expire in less than 5 minutes.
            </Typography>
            <Typography
              fontWeight="bold"
              align="center"
              sx={{
                color: "#474747",
                mt: { xs: 0.5, sm: 1 },
                fontSize: { xs: "0.875rem", sm: "1rem" },
                px: { xs: 1, sm: 0 },
              }}
            >
              Would you like to extend your session?
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "center",
                gap: { xs: 1.5, sm: 2 },
                mt: { xs: 3, sm: 4 },
                px: { xs: 1, sm: 0 },
              }}
            >
              <Button
                variant="outlined"
                onClick={handleDontExtend}
                disabled={extendLoading}
                sx={{
                  width: { xs: "100%", sm: "10rem" },
                  minWidth: { xs: "auto", sm: "10rem" },
                }}
              >
                Don't Extend
              </Button>
              <LoadingButton
                loading={extendLoading}
                sx={{
                  width: { xs: "100%", sm: "10rem" },
                  minWidth: { xs: "auto", sm: "10rem" },
                }}
                color="primary"
                onClick={handleExtendSession}
                variant="contained"
              >
                Extend Session
              </LoadingButton>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default SalesSessionWillExpireBox;
